/** Escala de render do PDF — funções puras, testáveis sem DOM.
 *
 * Existe UMA escala por página, e ela precisa concordar em três lugares: a caixa
 * do wrapper, o viewport passado ao canvas e o `--scale-factor` que a camada de
 * texto lê. Se divergirem, o texto não fica "um pouco torto": o `TextLayer` do
 * pdf.js escreve `font-size: calc(var(--scale-factor) * Npx)` em cada span e
 * posiciona por porcentagem da caixa — a seleção sai deslocada e todo destaque
 * por coordenada nasce errado.
 */

export interface Tamanho {
  width: number
  height: number
}

/** Teto de pixels do bitmap do canvas.
 *
 * ⚠️ O Safari no iPhone recusa canvas acima de ~16,7 MPx devolvendo canvas em
 * BRANCO, sem lançar erro — o aluno vê a página sumir e não há o que depurar.
 */
export const MAX_CANVAS_PIXELS = 16_777_216

/** Escala máxima de leitura; acima disso o ganho é ilusório e a memória não. */
export const ESCALA_MAX = 4

export type ModoDeAjuste = 'largura' | 'pagina' | 'fixo'

/** Escala em CSS px para caber no container, conforme o modo escolhido. */
export function fitScale(
  pagina: Tamanho,
  container: Tamanho,
  modo: ModoDeAjuste = 'largura',
  fixo = 1,
): number {
  if (modo === 'fixo') return limitarEscala(fixo)
  if (pagina.width <= 0 || pagina.height <= 0) return 1
  if (container.width <= 0) return 1

  const porLargura = container.width / pagina.width
  if (modo === 'largura') return limitarEscala(porLargura)

  const porAltura = container.height > 0 ? container.height / pagina.height : porLargura
  return limitarEscala(Math.min(porLargura, porAltura))
}

export function limitarEscala(escala: number): number {
  if (!Number.isFinite(escala) || escala <= 0) return 1
  return Math.min(escala, ESCALA_MAX)
}

/** Densidade efetiva do bitmap, respeitando o orçamento de pixels.
 *
 * A redução é em CASCATA: primeiro cai a densidade (3 → 2 → 1), e só depois o
 * chamador limita a escala. Degradar a nitidez é aceitável; canvas em branco
 * não é.
 */
export function dprEfetivo(
  cssWidth: number,
  cssHeight: number,
  dpr: number,
  teto: number = MAX_CANVAS_PIXELS,
): number {
  const area = Math.max(1, cssWidth * cssHeight)
  const desejado = Number.isFinite(dpr) && dpr > 0 ? dpr : 1
  const maximo = Math.sqrt(teto / area)
  return Math.max(1, Math.min(desejado, maximo))
}

/** A página ainda cabe no orçamento de pixels com esta escala e densidade? */
export function cabeNoOrcamento(
  pagina: Tamanho,
  escala: number,
  dpr: number,
  teto: number = MAX_CANVAS_PIXELS,
): boolean {
  return pagina.width * escala * dpr * (pagina.height * escala * dpr) <= teto
}
